import BaseComponent from '../BaseComponent';
import Manipulator from '../../utils/dom/manipulator';
import SelectorEngine from '../../utils/dom/selector-engine';
import dayjs from 'dayjs';
import EventHandler from '../../utils/dom/event-handler';

const NAME = 'DateRangePicker';
const CLICK_EVENT = 'click';
const DATE_RANGE_PICKER_LABEL = '.lta-date-range-picker-label';
const DATE_RANGE_PICKER_INPUT = '.lta-date-range-picker__input';
const DATE_RANGE_PICKER_INPUT_LOWER_DATE_RANGE = '.date-range-picker__input_lower_date_range';
const DATE_RANGE_PICKER_INPUT_UPPER_DATE_RANGE = '.date-range-picker__input_upper_date_range';
const DATE_RANGE_PICKER_WRAPPER = '.lta-date-range-picker__wrapper';
const DATE_RANGE_PICKER_LABEL_ICON = '.lta-date-range-picker__label-icon';

class DateRangePicker extends BaseComponent {
	constructor(element) {
		super(element);
		if (element) {
			this.init(element);
		}
	}

	static get NAME() {
		return NAME;
	}

	init(element) {
		this.dateRangeWrapper = SelectorEngine.findOne(DATE_RANGE_PICKER_WRAPPER, element);
		this.inputDateRangeElement = SelectorEngine.findOne(DATE_RANGE_PICKER_INPUT, this.dateRangeWrapper);
		this.inputLowerDateRangeElement = SelectorEngine.findOne(DATE_RANGE_PICKER_INPUT_LOWER_DATE_RANGE, this.dateRangeWrapper);
		this.inputUpperDateRangeElement = SelectorEngine.findOne(DATE_RANGE_PICKER_INPUT_UPPER_DATE_RANGE, this.dateRangeWrapper);
		this.dateDisplayIconLabelWrapper = SelectorEngine.findOne(DATE_RANGE_PICKER_LABEL_ICON, this.dateRangeWrapper);
		this.dateDisplayLabel = SelectorEngine.findOne(DATE_RANGE_PICKER_LABEL, this.dateRangeWrapper);

		this.maxDateData = Manipulator.getDataAttribute(this.inputDateRangeElement, 'maxDate');
		this.minDateData = Manipulator.getDataAttribute(this.inputDateRangeElement, 'minDate');
		this.valueDateData = Manipulator.getDataAttribute(this.inputDateRangeElement, 'value');
		this.rangeData = Manipulator.getDataAttribute(this.inputDateRangeElement, 'range');
		this.showInput = Manipulator.getDataAttribute(this.inputDateRangeElement, 'input');

		this.defaultValue = [];
		if (this.valueDateData) {
			const defaultValueArray = this.valueDateData.split(',');
			if (defaultValueArray?.length === 2) {
				this.defaultValue = defaultValueArray;
			}
		}

		this.flatpickrInstance = flatpickr(this.inputDateRangeElement, {
			mode: 'range',
			dateFormat: 'Y-m-d',
			minDate: this.minDateData,
			maxDate: this.maxDateData,
			defaultDate: this.defaultValue.map((date) => date),
			onClose: (selectedDates) => {
				this.defaultValue = selectedDates.map((date) =>
					this.showInput ? this.convertDateFormate(selectedDates?.[0]) : new Date(date)
				);
				if (!this.showInput) {
					this.updateDateDisplayLabel();
				}
				else {
					const formate1 = this.convertDateFormate(selectedDates?.[0])
					const formate2 = this.convertDateFormate(selectedDates?.[1])
					this.inputLowerDateRangeElement.setAttribute('value', formate1);
					this.inputUpperDateRangeElement.setAttribute('value', formate2);
				}
			},
			onOpen: (selectedDates, dateStr, instance) => {
				if(!!this.rangeData){
					const existingErrorMessage = instance.calendarContainer.querySelector('#error-message');
					if (!existingErrorMessage) {
						const message = `Please note: your date range must not exceed ${this.rangeData} days`;
						const messageTag = `<span class="flatpickr-calendar-message">${message}</span>`;
						instance.calendarContainer.querySelector('.flatpickr-calendar-message')?.remove()
						instance.calendarContainer.insertAdjacentHTML('beforeend', messageTag);
						instance.open();
					}
				}

				const monthClass = SelectorEngine.findOne('.numInputWrapper', instance.calendarContainer);
				const inputElement = SelectorEngine.findOne('.cur-year', monthClass);
				const upButton = SelectorEngine.findOne('.arrowUp', monthClass);
				const downButton = SelectorEngine.findOne('.arrowDown', monthClass);
				
				const max = parseInt(inputElement.getAttribute('max'));
				const min = parseInt(inputElement.getAttribute('min'));
				inputElement.readOnly = true;

				function updateButtonVisibility(value) {
					const isPreviousEnabled = value < max;
					const isNextEnabled = value > min;

					upButton.classList.remove('u-hidden');
					upButton.classList.remove('previous-only');
					downButton.classList.remove('u-hidden');
					downButton.classList.remove('next-only');

					if(isPreviousEnabled && !isNextEnabled) {
						upButton.classList.add('u-hidden');
						downButton.classList.add('next-only')
					} else if(!isPreviousEnabled && isNextEnabled) {
						upButton.classList.add('previous-only');
						downButton.classList.add('u-hidden')
					} else if(!isPreviousEnabled && !isNextEnabled) {
						upButton.classList.add('u-hidden') 
						downButton.classList.add('u-hidden');
					}
				}

				upButton.onclick = () => {
					updateButtonVisibility(parseInt(inputElement.value) + 1);
				}
				downButton.onclick = () => {
					updateButtonVisibility(parseInt(inputElement.value) - 1);
				}
				updateButtonVisibility(parseInt(inputElement.value));
			}
		});

		EventHandler.on(this.dateDisplayIconLabelWrapper, CLICK_EVENT, () => {
			this.flatpickrInstance.open();
		});

		this.updateDateDisplayLabel();
	}

	updateDateDisplayLabel() {
		if (this.defaultValue.length === 2) {
			const [startDate, endDate] = this?.defaultValue?.map((item) => this.convertLabelFormate(item));
			this.dateDisplayLabel.textContent = `${startDate} to ${endDate}`;
		} else {
			this.dateDisplayLabel.textContent = 'Select Date:';
		}
	}

	convertDateFormate(dateString) {
		return dayjs(dateString).format("YYYY-MM-DD");
	}

	convertLabelFormate(dateString) {
		if (dateString?.toString()?.split('-')?.length === 3) {
			const dashFormate = !!dateString?.includes('/') ? dateString?.split('/')?.join('-') : dateString;
			const splitedArray = dashFormate?.split('-');
			const chnageformate = splitedArray[1] + '-' + splitedArray[0] + '-' + splitedArray[2];
			const parsedDate = dayjs(chnageformate, 'MM-DD-YYYY');
			const formattedDate = parsedDate?.format('MMMM DD[th], YYYY');
			return formattedDate;
		} else {
			const parsedDate = dayjs(new Date(dateString), 'YYYY-MM-DDTHH:mm:ss.SSSZ');
			const formattedDate = parsedDate?.format('MMMM DD[th], YYYY');
			return formattedDate;
		}
	}
}

export default DateRangePicker;
